import { ShowByButtons } from '@src/containers/Portfolio/HistoricalBalance/components/ShowByButtons';
import type { Balance } from '@src/types';
import {
  FormControlSizes,
  MobileDataTable,
  WSBlotterTableMaxRecordsReachedWarning,
  type MobileCardRendererProps,
} from '@talos/kyoko';
import { useCallback } from 'react';
import type { BalancesV2TabProps } from '../../../../Blotters/BalancesV2/types';
import { useBalancesV2BlotterFoundation } from '../../../../Blotters/BalancesV2/useBalancesV2BlotterFoundation';
import { MobileBlotterToolbar } from '../MobileBlotterToolbar';
import { MobileBalanceCard, useMobileBalanceCardLayout } from './MobileBalanceCard';
import { MobileBalanceGroupCard } from './MobileBalanceGroupCard';
import { MobileBalancesSettingsToggles } from './MobileBalancesSettingsToggles';
import { renderBalanceWarningDetails } from './mobileBalanceWarnings';

interface MobileFilteredBalancesProps {
  blotterID: string;
  tab: BalancesV2TabProps;
  onUpdateTab: (updatedTab: BalancesV2TabProps) => void;
}

export function MobileFilteredBalances({ blotterID, tab, onUpdateTab }: MobileFilteredBalancesProps) {
  const { cardHeight, cardRendererProps, compact } = useMobileBalanceCardLayout();
  const {
    blotterTable,
    filterBuilderAccordion,
    groupAccounts,
    handleGroupAccountsToggle,
    handleShowByChanged,
    showBy,
    showZeroBalances,
    updateShowZeroBalances,
  } = useBalancesV2BlotterFoundation({
    blotterID,
    tab,
    onUpdateTab,
    enableTotals: false,
    groupHideParentOfSingleChild: !compact,
  });
  const cardRenderer = useCallback(
    (props: MobileCardRendererProps<Balance>) => <MobileBalanceCard {...props} showBy={showBy} />,
    [showBy]
  );

  return (
    <>
      <MobileBlotterToolbar
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={
          <ShowByButtons showBy={showBy} onClick={handleShowByChanged} size={FormControlSizes.Default} />
        }
        settingsContent={
          <MobileBalancesSettingsToggles
            groupAccounts={groupAccounts}
            onGroupAccountsToggle={handleGroupAccountsToggle}
            onShowZeroBalancesToggle={updateShowZeroBalances}
            showBy={showBy}
            showZeroBalances={showZeroBalances}
          />
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning
        {...blotterTable.paginationLimit}
        getTimestamp={balance => balance.LastUpdateTime}
      />
      <MobileDataTable<Balance>
        {...blotterTable}
        cardRenderer={cardRenderer}
        cardRendererProps={cardRendererProps}
        groupCardRenderer={MobileBalanceGroupCard}
        cardHeight={cardHeight}
        detailsTitle="Balance Details"
        rowDetailHeader={renderBalanceWarningDetails}
      />
    </>
  );
}
